import { useEffect, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import TagManager from 'react-gtm-module';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import { isBrowser } from 'utils/misc-utils';
import { embeddedEventDispatcher } from 'utils/analytics/helpers/gtag';

export function pushGTMDataLayer(payload, includeChannel = false, channel = null) {
  const enhancedPayload = includeChannel && channel ? { ...payload, channel } : payload;

  if (window.__DEBUG_GTM) {
    console.log('[dutchie] GTM DataLayer: ', enhancedPayload);
  }
  if (window.dataLayer) {
    window.dataLayer.push(enhancedPayload);
  }
  embeddedEventDispatcher('dataLayer', 'push', enhancedPayload);
}

/**
 * Get the appropriate GTM container ID for the current channel
 * @param {Object} dispensary - The dispensary object
 * @param {'storefront'|'kiosk'|'embedded'|'mobileApp'|'default'} channel - The current channel
 * @returns {string|undefined} - The GTM container ID or undefined if not configured
 */
export function getChannelSpecificGTMContainerID(dispensary, channel) {
  const gtmConfig = dispensary?.googleTagManager;

  if (!gtmConfig) {
    return undefined;
  }

  // Try channel-specific container ID first
  const channelSpecificId = gtmConfig?.containerIds?.[channel];
  if (channelSpecificId) {
    return channelSpecificId;
  }

  // Fall back to default container ID
  const defaultId = gtmConfig?.containerIds?.default;
  if (defaultId) {
    return defaultId;
  }

  // Final fallback to legacy gtmID
  return gtmConfig?.gtmID;
}

/**
 * Determine the current channel based on UI state
 * @param {Object} UI - The UI store
 * @returns {'storefront'|'kiosk'|'embedded'|'mobileApp'|'default'} - The current channel
 */
export function getCurrentChannel(UI) {
  if (UI.isStoreFront) {
    return 'storefront';
  }
  if (UI.isKiosk) {
    return 'kiosk';
  }
  if (UI.isEmbedded) {
    return 'embedded';
  }
  if (UI.isMobileEcommApp) {
    return 'mobileApp';
  }
  return 'default';
}

// Keep track of initialized trackers to avoid duplicate initialization
const initializedTrackers = new Set();

function initDispensaryTracker(id, unrestrictedTags = false) {
  if (!id || initializedTrackers.has(id)) {
    return;
  }

  let dataLayerConfig = {};
  if (!unrestrictedTags) {
    dataLayerConfig = {
      'gtm.allowlist': ['google', 'customPixels', 'flc', 'fls', 'twitter_website_tag'],
    };
  }

  TagManager.initialize({
    gtmId: id,
    dataLayer: {
      ...dataLayerConfig,
    },
  });

  initializedTrackers.add(id);
}

export function useDispensaryTagManager() {
  const UI = useUI();
  const { dispensary } = useDispensary();
  const ldFlags = useFlags();

  // Determine if channel-specific GTM is enabled via feature flag
  const channelSpecificGTMEnabled = ldFlags?.['ecomm.gtm.channel-specific-containers.rollout'] ?? false;

  // Determine current channel
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentChannel = useMemo(() => getCurrentChannel(UI), [
    UI.isStoreFront,
    UI.isKiosk,
    UI.isEmbedded,
    UI.isMobileEcommApp,
  ]);

  // Always run dispensary GTM for embedded, storefront, plus, and kiosk when feature flag is enabled
  const shouldUseDispensaryGTM = useObserver(
    () => UI.isEmbedded || UI.isStoreFront || UI.isPlus || (channelSpecificGTMEnabled && UI.isKiosk)
  );

  return useEffect(() => {
    if (!isBrowser() || !shouldUseDispensaryGTM || !dispensary) {
      return;
    }

    let gtmContainerID;
    const unrestrictedTags = dispensary.googleTagManager?.flags?.unrestricted_tags;

    if (channelSpecificGTMEnabled) {
      // Use channel-specific container ID
      gtmContainerID = getChannelSpecificGTMContainerID(dispensary, currentChannel);
    } else {
      // Use legacy single container ID
      gtmContainerID = dispensary?.googleTagManager?.gtmID;
    }

    // Initialize GTM if we have a container ID
    if (gtmContainerID) {
      initDispensaryTracker(gtmContainerID, unrestrictedTags);
    }

    // Push identifying data to data layer with channel information
    pushGTMDataLayer(
      {
        retailerId: dispensary.id,
        retailerName: dispensary.name,
        retailerAddress: dispensary.address,
      },
      channelSpecificGTMEnabled,
      currentChannel
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispensary?.id,
    dispensary?.name,
    dispensary?.address,
    dispensary?.googleTagManager,
    shouldUseDispensaryGTM,
    currentChannel,
    channelSpecificGTMEnabled,
  ]);
}
